import { auth } from '../firebase'; // Import Firebase auth instance

const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:3800";

/**
 * Fetches the list of service types from the backend.
 * @returns {Promise<Array<object>>} - A promise that resolves to an array of service type objects.
 * @throws {Error} - Throws an error if the fetch fails or the user is not authenticated.
 */
export const listServiceTypes = async () => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/service-types/list`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    if (responseData.status !== 'success') {
        throw new Error(responseData.message || 'Failed to list service types');
    }

    // Assuming the API returns { status: 'success', data: { serviceTypes: [...] } }
    return responseData.data?.serviceTypes || []; // Return the serviceTypes array or an empty array

  } catch (error) {
    console.error("Error listing service types:", error);
    throw error; // Re-throw the error
  }
};

/**
 * Adds a new service type via the backend API.
 * @param {string} name - The name of the service type to add.
 * @returns {Promise<object>} - The response data from the API, including the new service type details.
 * @throws {Error} - Throws an error if the request fails, the name is missing, or the user is not authenticated.
 */
export const addServiceType = async (name) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }
  if (!name || typeof name !== 'string' || name.trim() === '') {
    throw new Error("Service name is required and must be a non-empty string.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/service-types/add`;
    const payload = { name: name.trim() };

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const responseData = await response.json();

    if (!response.ok || responseData.status !== 'success') {
      // Use the message from the API response if available
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    // Return the full response which includes { status, message, data: { newServiceType } }
    return responseData;

  } catch (error) {
    console.error("Error adding service type:", error);
    // Check if the error message is already specific (e.g., from the API)
    // If not, provide a generic fallback
    throw new Error(error.message || "Failed to add service type. Please try again.");
  }
};

/**
 * Renames an existing service type via the backend API.
 * @param {string} serviceTypeId - The ID of the service type to rename.
 * @param {string} newName - The new name for the service type.
 * @returns {Promise<object>} - The response data from the API.
 * @throws {Error} - Throws an error if the request fails, data is missing, or the user is not authenticated.
 */
export const renameServiceType = async (serviceTypeId, newName) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }
  if (!serviceTypeId || !newName || typeof newName !== 'string' || newName.trim() === '') {
    throw new Error("Service Type ID and a non-empty new name are required.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/service-types/rename`;
    const payload = { serviceTypeId, newName: newName.trim() };

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const responseData = await response.json();

    if (!response.ok || responseData.status !== 'success') {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    return responseData; // { status, message, data: { id, name } }

  } catch (error) {
    console.error("Error renaming service type:", error);
    throw new Error(error.message || "Failed to rename service type.");
  }
};

/**
 * Deletes a service type via the backend API.
 * @param {string} serviceTypeId - The ID of the service type to delete.
 * @returns {Promise<object>} - The response data from the API.
 * @throws {Error} - Throws an error if the request fails, ID is missing, or the user is not authenticated.
 */
export const deleteServiceType = async (serviceTypeId) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }
  if (!serviceTypeId) {
    throw new Error("Service Type ID is required.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/service-types/delete`;
    const payload = { serviceTypeId };

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const responseData = await response.json();

    if (!response.ok || responseData.status !== 'success') {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    return responseData; // { status, message }

  } catch (error) {
    console.error("Error deleting service type:", error);
    throw new Error(error.message || "Failed to delete service type.");
  }
};
